import { formatCurrency, formatDate } from '@if/core/utils'
import { AnyError, useConfirmDialog, useErrorHandler } from '@if/mui'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import {
  CalendarSimulationAction,
  ECalendarSimulationAction,
  ExistingFee,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AddFeeSchemaType } from './AddFee-schema'
import { useAddFeeDialog } from './AddFeeDialog'
import { EditFeeSchemaType } from './EditFee-schema'
import { useEditFeeDialog } from './EditFeeDialog'

/** Recomputes the calendar with one fee action. Throws on failure: a dialog running it stays open and shows the error. */
const useComputeFeeAction = (signedSimulation: SignedCalendarSimulation) => {
  const { loan } = signedSimulation.simulation.fullLoan
  const { mutateAsync: computeAction } = useComputeCalendarAction(loan.id, loan.financingCompanyId)

  return useCallback(
    (action: CalendarSimulationAction): Promise<SignedCalendarSimulation> =>
      computeAction({ ...signedSimulation, action }),
    [computeAction, signedSimulation],
  )
}

/** Adds one fee from the calendar: the fee form recomputes the calendar on confirm. Null when the agent cancels. */
export const useAddFeeOnCalendar = (signedSimulation: SignedCalendarSimulation, isLockedByOther: boolean) => {
  const { loan } = signedSimulation.simulation.fullLoan
  const computeFeeAction = useComputeFeeAction(signedSimulation)
  const onSubmit = useCallback(
    (fee: AddFeeSchemaType) =>
      computeFeeAction({
        actionType: ECalendarSimulationAction.AddFee,
        effectiveDate: fee.effectiveDate,
        amount: fee.amount,
        feeType: fee.feeType,
        note: fee.note || undefined,
      }),
    [computeFeeAction],
  )
  const addFeeDialog = useAddFeeDialog({ activationDate: loan.activationOn, isLockedByOther, onSubmit })

  return useCallback((): Promise<SignedCalendarSimulation | null> => addFeeDialog.create(), [addFeeDialog])
}

/** Edits one fee from its calendar row, on the same form. Null when the agent cancels. */
export const useEditFeeOnCalendar = (signedSimulation: SignedCalendarSimulation, isLockedByOther: boolean) => {
  const { loan } = signedSimulation.simulation.fullLoan
  const computeFeeAction = useComputeFeeAction(signedSimulation)
  const onSubmit = useCallback(
    (edited: EditFeeSchemaType, fee: ExistingFee) =>
      computeFeeAction({
        actionType: ECalendarSimulationAction.EditFees,
        editedFees: [
          {
            operationId: fee.operationId,
            effectiveDate: edited.effectiveDate,
            amount: edited.amount,
            note: edited.note ?? '',
          },
        ],
        cancelledFeeIds: [],
        reappliedFeeIds: [],
      }),
    [computeFeeAction],
  )
  const editFeeDialog = useEditFeeDialog({ activationDate: loan.activationOn, isLockedByOther, onSubmit })

  return useCallback(
    (fee: ExistingFee): Promise<SignedCalendarSimulation | null> => editFeeDialog.create(fee),
    [editFeeDialog],
  )
}

/** Cancels one fee from its calendar row, once the agent confirms. Null when the agent backs out. */
export const useCancelFeeOnCalendar = (signedSimulation: SignedCalendarSimulation, isLockedByOther: boolean) => {
  const { t } = useTranslation()
  const computeFeeAction = useComputeFeeAction(signedSimulation)
  // A confirm dialog resolves with a boolean only: the recomputed calendar comes back through here.
  const resultRef = useRef<SignedCalendarSimulation | null>(null)

  const cancelFee = useCallback(
    (fee: ExistingFee): Promise<SignedCalendarSimulation> =>
      computeFeeAction({
        actionType: ECalendarSimulationAction.EditFees,
        editedFees: [],
        cancelledFeeIds: [fee.operationId],
        reappliedFeeIds: [],
      }),
    [computeFeeAction],
  )

  const confirm = useConfirmDialog<ExistingFee>({
    title: t('loan.feesDialog.cancelFee'),
    message: ({ props: fee }) =>
      t('loan.feesDialog.cancelFeeMessage', {
        feeType: t(`enum.EFeeType.${fee.feeType}`),
        amount: formatCurrency(fee.amount),
        date: formatDate(fee.effectiveDate),
      }),
    confirmLabel: t('loan.feesDialog.cancelFee'),
    mode: 'destructive',
    confirmDisabled: () => isLockedByOther,
    confirmTooltip: () => (isLockedByOther ? t('common.lockTooltip.lockedByOther') : null),
    onConfirm: async ({ props: fee }) => {
      resultRef.current = await cancelFee(fee)
    },
  })

  return useCallback(
    async (fee: ExistingFee): Promise<SignedCalendarSimulation | null> => {
      resultRef.current = null
      return (await confirm.open(fee)) ? resultRef.current : null
    },
    [confirm],
  )
}

/** Reapplies one cancelled fee from its calendar row; its status follows its date again. Null when it fails (reported). */
export const useReapplyFeeOnCalendar = (signedSimulation: SignedCalendarSimulation) => {
  const computeFeeAction = useComputeFeeAction(signedSimulation)
  const { report } = useErrorHandler()

  return useCallback(
    (fee: ExistingFee): Promise<SignedCalendarSimulation | null> =>
      computeFeeAction({
        actionType: ECalendarSimulationAction.EditFees,
        editedFees: [],
        cancelledFeeIds: [],
        reappliedFeeIds: [fee.operationId],
      }).catch((error: unknown) => {
        report(error as AnyError)
        return null
      }),
    [computeFeeAction, report],
  )
}
